import { Example } from "../models/ExampleModel";
import { ExplanationModel } from "../models/ExplanationModel";
import { VerbConjugation } from "../models/VerbConjugationModel";

// Preterito Perfecto Explanation
export const preteritoPerfectoExplanation: ExplanationModel = {
    title: "📖 What is Pretérito Perfecto?",
    description:
        "The pretérito perfecto is used to describe actions that happened in the past but are still relevant in the present. It often appears with expressions that connect past actions to the present moment, such as hoy (today), esta semana (this week), or alguna vez (ever).",
    bulletPoints: [
        "Expresses past actions with a present connection (e.g., He comido mucho hoy - I have eaten a lot today).",
        "Often used with time markers like: hoy, esta semana, este mes, este año, alguna vez.",
        "Formed with the auxiliary verb haber (in present tense) + the past participle (participio pasado).",
        "Regular past participles follow these patterns: \n   - '-ar' → '-ado' (e.g., hablar → hablado) \n   - '-er' / '-ir' → '-ido' (e.g., comer → comido, vivir → vivido).",
        "Some verbs have irregular past participles (e.g., hacer → hecho, ver → visto, escribir → escrito).",
    ],
};

// Preterito Perfecto Examples Data
export const preteritoPerfectoExamplesData: Example[] = [
    {
        sentence: "¿Alguna vez has probado sushi?",
        explanation:
            "🍣 Experience – 'Have you ever tried sushi?' The present perfect is often used to ask about life experiences, focusing on whether something has happened rather than when.",
    },
    {
        sentence: "Hoy he comido sushi.",
        explanation:
            "📅 Recent Action – 'Today I have eaten sushi.' The action happened in the past but is still relevant today, which is a key use of the pretérito perfecto.",
    },
    {
        sentence: "Este año hemos viajado mucho.",
        explanation:
            "✈️ Ongoing Relevance – 'This year we have traveled a lot.' The use of 'este año' links the action to an unfinished time period.",
    },
    {
        sentence: "Últimamente, he estado muy ocupado.",
        explanation:
            "⏳ Ongoing Situation – 'Lately, I have been very busy.' 'Últimamente' suggests an action that started in the past but continues affecting the present.",
    },
    {
        sentence: "Mis amigos han visto esa película.",
        explanation:
            "🎬 Completed Action with Present Relevance – 'My friends have seen that movie.' The action is complete, but it is still relevant to the conversation.",
    },
    {
        sentence: "No he terminado mi trabajo todavía.",
        explanation:
            "🚧 Unfinished Action – 'I haven’t finished my work yet.' The word 'todavía' (yet) emphasizes that the action remains incomplete.",
    },
]

// Preterito Perfecto Regular Conjugation
export const preteritoPerfectoRegular: VerbConjugation[] = [
    { verb: "Hablar", conjugations: ["he hablado", "has hablado", "ha hablado", "hemos hablado", "habéis hablado", "han hablado"] },
    { verb: "Comer", conjugations: ["he comido", "has comido", "ha comido", "hemos comido", "habéis comido", "han comido"] },
    { verb: "Vivir", conjugations: ["he vivido", "has vivido", "ha vivido", "hemos vivido", "habéis vivido", "han vivido"] },
];

// Preterito Perfecto Irregular Conjugation
export const preteritoPerfectoIrregular: VerbConjugation[] = [
    { verb: "Abrir", conjugations: ["he abierto", "has abierto", "ha abierto", "hemos abierto", "habéis abierto", "han abierto"] },
    { verb: "Escribir", conjugations: ["he escrito", "has escrito", "ha escrito", "hemos escrito", "habéis escrito", "han escrito"] },
    { verb: "Decir", conjugations: ["he dicho", "has dicho", "ha dicho", "hemos dicho", "habéis dicho", "han dicho"] },
    { verb: "Ver", conjugations: ["he visto", "has visto", "ha visto", "hemos visto", "habéis visto", "han visto"] },
    { verb: "Hacer", conjugations: ["he hecho", "has hecho", "ha hecho", "hemos hecho", "habéis hecho", "han hecho"] },
];

